import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { supplyPointQueries } from '@/features/supply-points/queries';
import { SupplyPointContext } from '@/features/supply-points/context/SupplyPointContext';
import { ACTIVE_SUPPLY_POINT_STORAGE_KEY } from '@/features/supply-points/config';
import {
  getStoredSupplyPointId,
  resolveActiveSupplyPointId,
} from '@/features/supply-points/utils/resolveActiveSupplyPoint';

export interface SupplyPointProviderProps {
  children: ReactNode;
}

export function SupplyPointProvider({ children }: SupplyPointProviderProps) {
  const { data: supplyPoints } = useSuspenseQuery(supplyPointQueries.list());
  const router = useRouter();

  const [activeId, setActiveId] = useState<number | null>(() => {
    const storedId = getStoredSupplyPointId();
    const resolved = resolveActiveSupplyPointId(supplyPoints, storedId);
    if (resolved !== null && resolved !== storedId) {
      localStorage.setItem(ACTIVE_SUPPLY_POINT_STORAGE_KEY, String(resolved));
    }
    return resolved;
  });

  const activeSupplyPoint = useMemo(
    () => supplyPoints.find((sp) => sp.id === activeId) ?? null,
    [supplyPoints, activeId]
  );

  const setActiveSupplyPointId = useCallback(
    (id: number) => {
      localStorage.setItem(ACTIVE_SUPPLY_POINT_STORAGE_KEY, String(id));
      setActiveId(id);
      void router.invalidate();
    },
    [router]
  );

  const value = useMemo(
    () => ({ supplyPoints, activeSupplyPoint, setActiveSupplyPointId }),
    [supplyPoints, activeSupplyPoint, setActiveSupplyPointId]
  );

  return <SupplyPointContext value={value}>{children}</SupplyPointContext>;
}
